import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, Button, CheckboxInput, Modal, SearchInput, Skeleton, Typography } from "@/components/atoms";
import { ACCESS_LIST_PAGE_SIZE, useGetAccessListPageQuery } from "@/redux/apis/assistants/assistantsApi";
import { useGetClientsQuery } from "@/redux/apis/client/clientApi";
import { GetClientsParams } from "@/redux/apis/client/types";
import { useListFavouritePassengersQuery } from "@/redux/apis/favouritePassenger/favouritePassengerApi";
import { FavouritePassenger } from "@/redux/apis/favouritePassenger/types";
import { useAppSelector } from "@/redux/hooks";
import { Client } from "@/redux/slices/client/types";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { getAssetUrl, getInitials } from "@/utils";

/** A row in the picker grid. Kept minimal so a non-client "(You)" row can be pinned by an EA flow. */
export interface ClientPickerProfile {
  id: string;
  name: string;
  avatarSrc?: string | null;
  /**
   * The client's account uuid. Same as `id` for the operator source; for the assistant source `id`
   * is the access-link id, so this carries the uuid separately. Callers that scope an API call
   * (rather than route on the id) need this one — e.g. bulk booking's `account_uuid`.
   */
  accountId?: string;
  /**
   * What the row is, so `onSelect` callers can branch on it — the three destinations differ
   * (self booking, the `book-for/:accessId` sheet, a guest booking on the EA's own account).
   * Also what the "Client" / "Guest" row label reads from.
   */
  kind?: "self" | "client" | "guest";
  /**
   * The saved favourite behind a `kind: "guest"` row. Rides along (as `accountId` does for bulk
   * booking) so the caller can seed the booking passenger without fetching the list a second time.
   */
  guest?: FavouritePassenger;
}

export interface ClientPickerModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Fires with the chosen profile's id — the client account id ("operator" source), the
   * access-link id ("assistant" source) or the favourite's uuid (a guest row). Callers reset the
   * draft + navigate; `profile.kind` says which destination.
   */
  onSelect: (id: string, profile?: ClientPickerProfile) => void;
  /**
   * Profiles pinned above the client list (e.g. the EA's own "(You)" row). Operators
   * pass none — this seam exists so the EA / travel-manager flow can reuse the modal.
   */
  pinnedProfiles?: ClientPickerProfile[];
  /**
   * Provider whose clients populate the grid. Defaults to the active operator; the EA /
   * travel-manager flow (which has no operator context) can pass its own provider or rely
   * solely on `pinnedProfiles` — so the modal never hard-depends on an active operator.
   */
  providerId?: string;
  /**
   * Data source for the grid. "operator" (default) lists the active provider's booking-eligible
   * clients; "assistant" (DEV-1296) lists the logged-in EA's CONNECTED managed clients and emits
   * the access-link id from `onSelect` so callers can route on `:accessId`.
   */
  source?: "operator" | "assistant";
  /**
   * Mixes the EA's own saved guests (My Favourites → Guests) into one alphabetical list and adds
   * the Clients/Guests filter row + people count (DEV-1593). Assistant source only. The bulk-upload
   * picker leaves it off: a bulk booking is always uploaded against a client account.
   */
  includeGuests?: boolean;
  /** Overrides the default heading — the bulk upload flow asks a different question. */
  title?: string;
  description?: string;
}

/** A client can hold a booking only while active — pending-invite and access-removed are excluded (DEV-1177 view-only parity). */
const isBookingEligible = (client: Client) => client.status === "active";

const guestName = (fav: FavouritePassenger) => [fav.first_name, fav.last_name].filter(Boolean).join(" ") || fav.email;

/**
 * A searchable, browsable grid of people to book on behalf of (Figma 11220-124406, 4002-27253).
 * Selecting one calls `onSelect` with the profile id. Three data sources, mixed per `source` /
 * `includeGuests`: the operator's booking-eligible clients (Clients-page provider list endpoint,
 * active only), the EA's CONNECTED managed clients (account-management access list, DEV-1296) and
 * the EA's own saved guests (favourite passengers, DEV-1593).
 */
export const ClientPickerModal = ({
  open,
  onClose,
  onSelect,
  pinnedProfiles = [],
  providerId,
  source = "operator",
  includeGuests = false,
  title,
  description,
}: ClientPickerModalProps) => {
  const isAssistant = source === "assistant";
  const withGuests = isAssistant && includeGuests;
  // Falls back to the active operator without throwing (unlike useOperator) so the EA reuse seam
  // can mount this modal outside operator context and drive it purely from pinnedProfiles.
  const activeOperator = useAppSelector(activeOperatorSelector);
  const operatorId = providerId ?? activeOperator?.id;
  const [search, setSearch] = useState<string>();
  // Inclusive filter: neither or both ticked shows everyone, so the filter alone can never empty
  // the list.
  const [filter, setFilter] = useState({ clients: false, guests: false });

  // Callers keep this mounted and just toggle `open` — and one instance serves both the booking and
  // the bulk picker — so without this the next opener inherits the last one's search and filter.
  useEffect(() => {
    if (!open) return;
    setSearch(undefined);
    setFilter({ clients: false, guests: false });
  }, [open]);

  const params: GetClientsParams = {
    provider_uuid: operatorId ?? "",
    account_status: ["active"],
    search,
    page_size: "100",
  };
  const { data, isFetching, isError } = useGetClientsQuery(params, { skip: !open || !operatorId || isAssistant });

  // Assistant source: the EA's CONNECTED managed clients, searched client-side (mirrors MyClients)
  // so typing never refetches; the profile id is the access-link id (`link.id`), which the caller
  // routes on — the client account uuid is resolved by the scoped booking wrapper via
  // useGetAccessQuery. `page_size` is explicit because the access route pages at 12 by default, and
  // with a client-side search that silently hid every client past the twelfth (DEV-1593).
  const {
    data: access,
    isFetching: accessFetching,
    isError: accessError,
  } = useGetAccessListPageQuery({ type: "YOU_MANAGE", status: "CONNECTED", page_size: ACCESS_LIST_PAGE_SIZE }, { skip: !open || !isAssistant });
  const accessLinks = access?.items ?? [];

  // Guests are the EA's OWN favourite passengers, so this goes unscoped (the active account) rather
  // than at a managed client — the same list My Favourites → Guests shows, and the account the
  // resulting booking is made under.
  const {
    data: favourites = [],
    isFetching: guestsFetching,
    isError: guestsError,
  } = useListFavouritePassengersQuery({}, { skip: !open || !withGuests });

  const query = search?.trim().toLowerCase() ?? "";
  const matchesQuery = (...fields: (string | null | undefined)[]) => !query || fields.some((field) => field?.toLowerCase().includes(query));

  const managedProfiles: ClientPickerProfile[] = accessLinks
    .filter((link) => matchesQuery(link.party.name, link.party.email))
    .map((link) => ({
      id: link.id,
      // The list query already filters to CONNECTED, so this uuid is safe to scope calls with.
      accountId: link.party.uuid,
      name: link.party.name || link.party.email,
      // party.avatar is a relative CDN key, not a URL — resolve it through the image handler (as
      // ClientAvatar does) or the <img> 404s against the app origin. Null when the client has none.
      avatarSrc: link.party.avatar ? getAssetUrl(link.party.avatar, { height: 120, width: 120, fit: "cover" }) : null,
      kind: "client",
    }));

  const guestProfiles: ClientPickerProfile[] = favourites
    .filter((fav) => matchesQuery(guestName(fav), fav.email))
    .map((fav) => ({ id: fav.uuid, name: guestName(fav), kind: "guest", guest: fav }));

  const eligibleClients = (data?.data.clients ?? []).filter(isBookingEligible);
  const listedProfiles: ClientPickerProfile[] = withGuests
    ? [...managedProfiles, ...guestProfiles].sort((a, b) => a.name.localeCompare(b.name))
    : isAssistant
    ? managedProfiles
    : eligibleClients.map((client) => ({ id: client.id, accountId: client.id, name: client.name }));

  const isKindVisible = ({ kind }: ClientPickerProfile) =>
    filter.clients === filter.guests || (filter.guests ? kind === "guest" : kind !== "guest");
  // The mixed list searches as one, so the pinned "(You)" row answers to the query too — searching
  // for someone else shouldn't leave it sitting there as the only "result".
  const pinned = withGuests ? pinnedProfiles.filter((profile) => matchesQuery(profile.name)) : pinnedProfiles;
  const profiles = [...pinned, ...listedProfiles].filter((profile) => !withGuests || isKindVisible(profile));

  // The grid shows one page of up to 100 for either source, so both can hold back clients the
  // server has. The operator's search is server-side and still reaches all of them, so that nudge
  // points at searching; the assistant's is client-side, so its line states the shortfall without
  // promising a way through it — see ACCESS_LIST_PAGE_SIZE.
  const totalActive = isAssistant ? (access?.total ?? 0) : (data?.data.page.total ?? 0);
  const listedCount = isAssistant ? accessLinks.length : eligibleClients.length;
  const isTruncated = totalActive > listedCount;

  const isListFetching = isAssistant ? accessFetching || (withGuests && guestsFetching) : isFetching;
  // Guests are an additive section — a failure there is reported under the grid rather than as the
  // list's error state, or an EA whose favourites endpoint is down couldn't reach their clients.
  const isListError = isAssistant ? accessError : isError;
  const guestsFailed = withGuests && !!guestsError;
  // The EA truly manages nobody (not a no-match search) — point them at My Clients to request access.
  const showAssistantEmptyState = isAssistant && !query && accessLinks.length === 0;
  // Guests-only filter with nothing saved — guests are added in My Favourites, never in this picker.
  const showGuestEmptyState = withGuests && !guestsFailed && !query && filter.guests && !filter.clients && favourites.length === 0;

  const defaultTitle = withGuests
    ? "Who is this trip for?"
    : isAssistant
    ? "Select a Client to Book a Trip"
    : "Select a Profile to Book a Trip";

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-large"
      title={title ?? defaultTitle}
      description={
        description ?? (withGuests ? "Search by name, or filter the list below." : "Choose a client to create a booking under it.")
      }
      renderButtons={() => (
        <Button variant="tertiary" size="md" onClick={onClose}>
          Cancel
        </Button>
      )}
    >
      <div className="flex flex-col gap-4">
        <SearchInput
          key={open ? "open" : "closed"}
          search={search}
          onSearch={setSearch}
          placeholder={withGuests ? "Search your clients or guests" : "Search user"}
          className="w-full [&>input]:w-full"
        />

        {withGuests && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Typography variant="paragraph" className="text-neutral-black">
              Filter
            </Typography>
            <CheckboxInput
              label="Clients"
              checked={filter.clients}
              onChange={(e) => setFilter((current) => ({ ...current, clients: e.target.checked }))}
            />
            <CheckboxInput
              label="Guests"
              checked={filter.guests}
              onChange={(e) => setFilter((current) => ({ ...current, guests: e.target.checked }))}
            />
            {!isListFetching && !isListError && (
              <Typography variant="paragraph" className="ml-auto text-neutral-dark-gray">
                {profiles.length === 1 ? "1 person" : `${profiles.length} people`}
              </Typography>
            )}
          </div>
        )}

        <div className="max-h-[45vh] overflow-y-auto">
          {isListFetching ? (
            <Skeleton />
          ) : isListError ? (
            <Typography variant="paragraph" className="py-8 text-center text-danger">
              Couldn&apos;t load your clients. Please try again.
            </Typography>
          ) : profiles.length ? (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {profiles.map((profile) => (
                  <ProfileCard key={profile.id} profile={profile} showKind={withGuests} onSelect={onSelect} />
                ))}
              </div>
              {isTruncated && (
                <Typography variant="paragraph" className="text-center text-neutral-dark-gray">
                  Showing the first {listedCount} of {totalActive} clients.
                  {!isAssistant && " Search by name to find anyone not listed."}
                </Typography>
              )}
            </div>
          ) : withGuests && query ? (
            <Typography variant="paragraph" className="py-8 text-center text-neutral-dark-gray">
              No one matches &apos;{search?.trim()}&apos;.
            </Typography>
          ) : showGuestEmptyState ? (
            <div className="flex flex-col items-center gap-1 py-8 text-center">
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                You haven&apos;t saved any guests yet
              </Typography>
              <Link to="/client/favourites/guests" onClick={onClose}>
                <Typography variant="action" className="text-primary">
                  Go to My Favourites
                </Typography>
              </Link>
            </div>
          ) : showAssistantEmptyState ? (
            <div className="flex flex-col items-center gap-1 py-8 text-center">
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                You aren&apos;t managing any clients yet.
              </Typography>
              <Link to="/client/my-clients" onClick={onClose}>
                <Typography variant="action" className="text-primary">
                  Go to My Clients
                </Typography>
              </Link>
            </div>
          ) : (
            <Typography variant="paragraph" className="py-8 text-center text-neutral-dark-gray">
              No clients found.
            </Typography>
          )}
          {guestsFailed && !isListFetching && !isListError && (
            <Typography variant="paragraph" className="pb-2 text-center text-danger">
              Couldn&apos;t load your guests. Please try again.
            </Typography>
          )}
        </div>
      </div>
    </Modal>
  );
};

interface ProfileCardProps {
  profile: ClientPickerProfile;
  /** Shows the "Client" / "Guest" cue under the name — only the mixed EA list needs telling apart. */
  showKind?: boolean;
  onSelect: (id: string, profile?: ClientPickerProfile) => void;
}

const ProfileCard = ({ profile, showKind, onSelect }: ProfileCardProps) => (
  <button
    type="button"
    onClick={() => onSelect(profile.id, profile)}
    className="flex w-full items-center gap-3 rounded-lg border border-neutral-mid-gray bg-white p-2.5 text-left transition-colors hover:border-primary hover:bg-primary-light"
  >
    <Avatar size="md" src={profile.avatarSrc ?? undefined} alt={profile.name} className="bg-neutral-mid-gray">
      {!profile.avatarSrc && (
        <Typography variant="action" className="text-neutral-dark-gray">
          {getInitials(profile.name)}
        </Typography>
      )}
    </Avatar>
    <div className="flex min-w-0 flex-col">
      <Typography variant="action" className="truncate text-neutral-black">
        {profile.name}
      </Typography>
      {showKind && (
        <Typography variant="small-regular" className="text-neutral-dark-gray">
          {profile.kind === "guest" ? "Guest" : "Client"}
        </Typography>
      )}
    </div>
  </button>
);
